import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, RainCheckPolicy } from '../api';
import { settingsEndpoints } from '../api';
import { Button, ErrorNote, Panel } from '../ui';
import { INPUT } from '../pages/event/shared';

/**
 * The rain-check policy (D-374). The owner's rain questions are still open,
 * so each is a setting with a default rather than a constant: a rain check is
 * good for this season (not any future game, not a number of days), it is
 * worth face value (not fees), and a rained-out game's makeup defaults to a
 * single-admission doubleheader. Whether a game was "official" is not here:
 * that is staff's judgment, made by pressing Rain out or not.
 *
 * A change applies to rain checks issued after it. Each rain check keeps the
 * last day it was given.
 */
export function RainCheckPolicyPanel() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['settings', 'rain-checks'], queryFn: settingsEndpoints.rainChecks });
  const [draft, setDraft] = useState<RainCheckPolicy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const current = draft ?? query.data?.policy ?? null;
  const save = useMutation({
    mutationFn: (input: RainCheckPolicy) => settingsEndpoints.updateRainChecks(input),
    onSuccess: () => {
      setError(null);
      setDraft(null);
      setNotice('Saved. Rain checks issued from now on follow it; ones already issued keep their dates.');
      void queryClient.invalidateQueries({ queryKey: ['settings', 'rain-checks'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  if (!current) return null;
  const set = (change: Partial<RainCheckPolicy>) => setDraft({ ...current, ...change });
  return (
    <Panel title="Rain checks" description="When a game is rained out, every ticket on it becomes a rain check: account credit for what was paid, spent on any future game at checkout or the window. Comps and season-pass tickets get nothing back; a used voucher is replaced.">
      <div className="space-y-4 p-5 text-sm">
        {error ? <ErrorNote>{error}</ErrorNote> : null}
        {notice ? <p className="text-ink-600">{notice}</p> : null}
        <fieldset className="space-y-1">
          <legend className="font-semibold text-ink-900">How long a rain check can be spent</legend>
          <label className="flex items-center gap-2">
            <input type="radio" name="rain-validity" checked={current.validity === 'SAME_SEASON'} onChange={() => set({ validity: 'SAME_SEASON', validity_days: null })} className="accent-brand" />
            Until the end of the rained-out game’s season
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="rain-validity" checked={current.validity === 'ANY_FUTURE'} onChange={() => set({ validity: 'ANY_FUTURE', validity_days: null })} className="accent-brand" />
            No end date (any future game)
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="rain-validity" checked={current.validity === 'DAYS'} onChange={() => set({ validity: 'DAYS', validity_days: current.validity_days ?? 365 })} className="accent-brand" />
            For
            <input type="number" min={1} max={3650} value={current.validity_days ?? 365} disabled={current.validity !== 'DAYS'} onChange={(e) => set({ validity: 'DAYS', validity_days: Math.max(1, Math.min(3650, Number(e.target.value || 1))) })} className={`${INPUT} w-24`} />
            days from the rainout
          </label>
          <p className="text-xs text-ink-500">What is unspent after the last day expires in the nightly sweep. Spending always uses the credit that runs out soonest first.</p>
        </fieldset>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={current.include_fees} onChange={(e) => set({ include_fees: e.target.checked })} className="h-4 w-4 accent-brand" />
          Fees come back too (off: face value only)
        </label>
        <fieldset className="space-y-1">
          <legend className="font-semibold text-ink-900">Makeup game</legend>
          <label className="flex items-center gap-2">
            <input type="radio" name="rain-dh" checked={current.doubleheader_default === 'SINGLE_ADMISSION'} onChange={() => set({ doubleheader_default: 'SINGLE_ADMISSION' })} className="accent-brand" />
            Single-admission doubleheader (one ticket, both games; rained-out holders use their rain checks)
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="rain-dh" checked={current.doubleheader_default === 'SEPARATE_ADMISSION'} onChange={() => set({ doubleheader_default: 'SEPARATE_ADMISSION' })} className="accent-brand" />
            Separate admission (postpone the game and set its new date; its tickets move to it)
          </label>
          <div className="flex flex-wrap gap-4 pt-1">
            <label className="block">
              <span className="block text-xs text-ink-400">Doubleheader title ({'{opponent}'} is the other team)</span>
              <input value={current.doubleheader_title} maxLength={120} onChange={(e) => set({ doubleheader_title: e.target.value })} className={`${INPUT} w-72`} />
            </label>
            <label className="block">
              <span className="block text-xs text-ink-400">Note on the game</span>
              <input value={current.doubleheader_note} maxLength={300} onChange={(e) => set({ doubleheader_note: e.target.value })} className={`${INPUT} w-72`} />
            </label>
          </div>
        </fieldset>
        <div className="flex items-center gap-3">
          <Button onClick={() => save.mutate(current)} disabled={save.isPending || !draft || !current.doubleheader_title.trim() || !current.doubleheader_note.trim()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {draft ? (
            <button type="button" className="text-sm text-ink-500 underline" onClick={() => { setDraft(null); setNotice(null); }}>
              Discard
            </button>
          ) : null}
        </div>
      </div>
    </Panel>
  );
}
